import React from "react";
import {
    type DynamicIslandHorizontalScrollOptions,
    type DynamicIslandVariantShellOptions,
} from "../dynamicIslandShell.types";
import DynamicIslandBackgroundShell from "./DynamicIslandBackgroundShell";
import DynamicIslandHorizontalScroll from "./DynamicIslandHorizontalScroll";

interface DynamicIslandVariantShellProps {

    /** Per-variant shell options from registry; omitted means no generic shell layers. */
    "shell": DynamicIslandVariantShellOptions | undefined;

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Registered variant content. */
    "children": React.ReactNode;
}

/**
 * Composes optional generic Dynamic Island shell layers around variant content.
 * Order (inner → outer): content → horizontal scroll → background.
 *
 * @param props Component props.
 * @returns Wrapped content or children unchanged when no shell options apply.
 */
const DynamicIslandVariantShell: React.FC<DynamicIslandVariantShellProps> = ( {
    shell,
    isDarkUi,
    children,
} ) => {
    if ( shell === undefined ) {
        return children;
    }

    let wrapped = children;

    if ( shell.horizontalScroll === true || typeof shell.horizontalScroll === "object" ) {
        const scrollOptions: DynamicIslandHorizontalScrollOptions = shell.horizontalScroll === true
            ? {}
            : shell.horizontalScroll;

        wrapped = (
            <DynamicIslandHorizontalScroll
                isDarkUi={isDarkUi}
                stepPx={scrollOptions.stepPx}
            >
                {wrapped}
            </DynamicIslandHorizontalScroll>
        );
    }

    if ( shell.background === true ) {
        wrapped = (
            <DynamicIslandBackgroundShell isDarkUi={isDarkUi}>
                {wrapped}
            </DynamicIslandBackgroundShell>
        );
    }

    return wrapped;
};

export default DynamicIslandVariantShell;
